<template>
  <div class="sena-games-pick">
    <va-card :title="cardTitle">
      <template slot="actions">
        <va-button
          color="gray"
          icon="fa fa-arrow-left"
          :to="{ name: 'sena-games', params: { edition: edition } }"
        />
      </template>

      <div class="sena-summary mb-3">
        <span class="sena-summary__item">
          <b>{{ pickedCount }}/6</b> {{ $t('sena.labels.gamesPicked') }}
        </span>
        <span class="sena-summary__item" v-if="!isDraft">
          <va-badge color="danger">{{ $t('sena.hints.gamesetFrozen') }}</va-badge>
        </span>
      </div>

      <div class="row mb-3">
        <div class="flex md6">
          <va-input
            v-model="search"
            :label="$t('sena.forms.searchLabel')"
            :placeholder="$t('sena.forms.searchPlaceholder')"
          />
        </div>
        <div class="flex md6">
          <va-toggle
            v-model="onlyFuture"
            small
            :label="$t('sena.forms.onlyFutureLabel')"
          />
        </div>
      </div>

      <va-data-table
        :fields="fields"
        :data="games"
        :per-page="perPage"
        :totalPages="totalPages"
        :loading="loading"
        @page-selected="showMore"
        no-data-label="Nenhum jogo encontrado"
        api-mode
      >
        <template slot="confronto" slot-scope="props">
          {{ props.rowData.club1.name }} <b>x</b> {{ props.rowData.club2.name }}
        </template>

        <template slot="actions" slot-scope="props">
          <va-badge v-if="isPicked(props.rowData.id)" color="success">
            {{ $t('sena.labels.picked') }}
          </va-badge>
          <va-button
            v-else
            flat
            color="gray"
            icon="fa fa-plus"
            :disabled="!isDraft || pickedCount >= 6 || busy"
            @click="pick(props.rowData)"
          />
        </template>
      </va-data-table>
    </va-card>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import { senaErrorMixin } from './senaErrors'

const perPage = 10

export default {
  name: 'SenaEditionGamesPick',
  mixins: [senaErrorMixin],
  props: {
    edition: { required: true },
  },
  data () {
    return {
      games: [],
      pages: null,
      picked: [],
      editionRow: null,
      page: 1,
      perPage: perPage,
      loading: false,
      busy: false,
      search: '',
      onlyFuture: true,
    }
  },
  computed: {
    // where compartilhado entre a lista e o aggregate: se divergirem,
    // a paginação passa a mentir sobre o total.
    where () {
      const w = {}
      if (this.onlyFuture) {
        w.datetime = { _gte: new Date().toISOString() }
      }
      if (this.search && this.search.trim()) {
        const term = `%${this.search.trim()}%`
        w._or = [
          { club1: { name: { _ilike: term } } },
          { club2: { name: { _ilike: term } } },
          { championship: { name: { _ilike: term } } },
        ]
      }
      return w
    },
    pickedCount () {
      return this.picked.length
    },
    isDraft () {
      return this.editionRow && this.editionRow.status === 'DRAFT'
    },
    cardTitle () {
      return this.editionRow
        ? `${this.$t('sena.cards.pickGames')} — ${this.editionRow.name}`
        : this.$t('sena.cards.pickGames')
    },
    totalPages () {
      return this.pages != null ? Math.ceil(this.pages.aggregate.count / this.perPage) : 0
    },
    fields () {
      const moment = this.$moment
      return [
        { name: 'id', title: this.$t('tables.id'), width: '70px' },
        { name: '__slot:confronto', title: this.$t('sena.headings.match') },
        {
          name: 'championship',
          title: this.$t('sena.headings.championship'),
          width: '20%',
          callback (value) {
            return value ? value.name : '—'
          },
        },
        {
          name: 'datetime',
          title: this.$t('sena.headings.datetime'),
          width: '180px',
          callback (value) {
            return moment(value).format('DD/MM/YYYY [às] HH:mm')
          },
        },
        {
          name: '__slot:actions',
          title: this.$t('tables.actions'),
          dataClass: 'text-center',
          titleClass: 'text-center',
          width: '110px',
        },
      ]
    },
  },
  apollo: {
    editionRow: {
      query: gql`
        query SenaEditionHead($edition: Int!) {
          editionRow: sena_edition_by_pk(id: $edition) {
            id name status
          }
        }
      `,
      variables () {
        return { edition: Number(this.edition) }
      },
      update: data => data.editionRow,
    },
    picked: {
      query: gql`
        query SenaPicked($edition: Int!) {
          picked: sena_edition_game(where: {sena_edition_id: {_eq: $edition}}, order_by: {order_index: asc}) {
            id
            game_id
            order_index
          }
        }
      `,
      variables () {
        return { edition: Number(this.edition) }
      },
      update: data => data.picked,
    },
    games: {
      query: gql`
        query SenaPickList($page: Int = 0, $perPage: Int = 10, $where: game_bool_exp!) {
          games: game(limit: $perPage, offset: $page, where: $where, order_by: {datetime: asc}) {
            id
            datetime
            club1 { id name }
            club2 { id name }
            championship { id name }
          }
        }
      `,
      variables () {
        return { perPage, page: 0, where: this.where }
      },
    },
    pages: {
      query: gql`
        query SenaPickCount($where: game_bool_exp!) {
          pages: game_aggregate(where: $where) {
            aggregate { count }
          }
        }
      `,
      variables () {
        return { where: this.where }
      },
    },
  },
  methods: {
    isPicked (gameId) {
      return this.picked.some(p => p.game_id === gameId)
    },
    showMore (page) {
      this.loading = true
      this.page = page
      this.$apollo.queries.games.fetchMore({
        variables: {
          page: (page - 1) * this.perPage,
          perPage: this.perPage,
          where: this.where,
        },
        updateQuery: (_, { fetchMoreResult }) => {
          this.loading = false
          return fetchMoreResult
        },
      })
    },
    pick (game) {
      // order_index tem UNIQUE(sena_edition_id, order_index): pega o primeiro
      // índice livre de 1..6 em vez de count+1, que colidiria depois de remover
      // um jogo do meio.
      const used = this.picked.map(p => p.order_index)
      const order = [1, 2, 3, 4, 5, 6].find(i => !used.includes(i))
      if (!order) {
        this.senaToastError(new Error('SENA_MAX_6_GAMES'), 'toasts.errorCreate')
        return
      }

      this.busy = true
      this.$apollo
        .mutate({
          mutation: gql`
            mutation PickGame($edition: Int!, $gameId: Int!, $order: Int!) {
              insert_sena_edition_game_one(object: {
                sena_edition_id: $edition,
                game_id: $gameId,
                order_index: $order
              }) {
                id
              }
            }
          `,
          variables: {
            edition: Number(this.edition),
            gameId: game.id,
            order,
          },
        })
        .then(() => {
          this.busy = false
          this.senaToastSuccess('toasts.successCreate')
          this.$apollo.queries.picked.refetch()
        })
        .catch(error => {
          this.busy = false
          this.senaToastError(error, 'toasts.errorCreate')
        })
    },
  },
}
</script>

<style lang="scss">
.sena-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  padding: 0.75rem 1rem;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 4px;
}
</style>
